import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Minus, Brain, ChevronDown, Check, BookOpen, Plug,
  MessageSquare, History, Layers, ShieldAlert, ChevronLeft, FolderKanban, Repeat,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { ChatMessageStream } from './ChatMessageStream';
import { ChatComposer } from './ChatComposer';
import { WorkspaceSidePanel } from './WorkspaceSidePanel';
import { ArtifactPanel } from './ArtifactPanel';
import { ResumeBar } from './ResumeBar';
import { PaFace } from '../avatar/PaFace';
import type { SideSection } from './SideNav';
import type { WorkspaceShellProps } from './WorkspaceShell';
import type { PaRoutine } from '@easyflow/shared';
import { Z_PA_SHELL, Z_PA_SHELL_OVERLAY, Z_PA_SHELL_DROPDOWN, Z_PA_SHELL_OVERLAY_TOP } from '@/constants/zLayers';

const MOBILE_TABS: Array<{ id: SideSection; label: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'chat',      label: 'Chat',      Icon: MessageSquare },
  { id: 'projects',  label: 'Progetti',  Icon: FolderKanban },
  { id: 'routines',  label: 'Routine',   Icon: Repeat },
  { id: 'rounds',    label: 'Attività',  Icon: History },
  { id: 'artifacts', label: 'Artefatti', Icon: Layers },
  { id: 'approvals', label: 'Conferme',  Icon: ShieldAlert },
  { id: 'memory',    label: 'Memoria',   Icon: Brain },
  { id: 'skills',    label: 'Skills',    Icon: BookOpen },
  // Task #3818 — Connettori: perimetro effettivo + restrizione per-chat.
  { id: 'connectors', label: 'Connettori', Icon: Plug },
];

const SECTION_TITLE: Record<SideSection, string> = {
  chat: 'Conversazioni',
  projects: 'Progetti',
  routines: 'Routine',
  rounds: 'Attività agente',
  artifacts: 'Artefatti',
  approvals: 'Conferme',
  memory: 'Memoria',
  skills: 'Skills (procedure)',
  connectors: 'Connettori',
};

// Vista mobile isolata (≤767px) della chat PA: frame full-screen dedicato che
// riusa ESCLUSIVAMENTE i sotto-componenti presentazionali condivisi. Nessuna
// logica/feature chat duplicata qui: solo il layout (header compatto, tab bar,
// overlay full-screen) cambia rispetto al desktop.
export const MobileShell: React.FC<WorkspaceShellProps> = (props) => {
  const {
    assistantId, assistantName, assistantColor, assistantDescription, userFirstName, AssistantIcon,
    conversations, currentConversationId, onSelectConversation, onNewConversation, onDeleteConversation, onBulkDeleteConversations,
    messages, streamingContent, streamingThinking, activeTools, activeRag, isStreaming, streamingStatus, inputValue, onInputChange, onSendMessage,
    attachedFiles, onUploadFiles, onRemoveFile, isRecording, onToggleRecording,
    workspaceState, rounds, artifacts, conversationArtifacts, approvals, onDecideApproval, onInterrupt, onResume, onArtifactAction, onDeleteArtifact,
    onArtifactFormSubmit, onRegenerate,
    conversationJobs, jobsLoading = false, jobsError = null, onRetryJobs, onDecideJob, onCancelJob,
    modelOptions = [], defaultModelId = null, currentModelId = null, modelSwitchEnabled = false, onModelChange,
    currentUserId = null, onNewProjectConversation,
    onOpenRoutineInChat, sectionRequest,
    slashSkills, invokedSkills, onInvokeSkill, onRemoveInvokedSkill,
    onCollapse, onClose,
    avatarEnabled = false, avatarConfig = null, avatarDefaultSeed = 'pa', faceState = 'idle', onOpenAvatarSettings,
  } = props;

  // Overlay sezione (tab bar): `null` = solo chat live. L'artefatto aperto vive
  // in un secondo overlay sopra a questo, così la chiusura del viewer torna alla
  // lista artefatti invece che alla chat.
  const [activeSection, setActiveSection] = useState<SideSection | null>(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const sectionOverlayRef = useRef<HTMLDivElement>(null);
  const artifactOverlayRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<SideSection, HTMLButtonElement>>>({});
  const selectedArtifactIdRef = useRef<string | null>(null);
  selectedArtifactIdRef.current = selectedArtifactId;

  const currentConversationTitle = useMemo(() => {
    const c = conversations.find(cc => cc.id === currentConversationId);
    return c?.title ?? null;
  }, [conversations, currentConversationId]);

  const currentModelLabel = useMemo(() => {
    const found = modelOptions.find(m => m.id === currentModelId);
    if (found) return found.displayName;
    return currentModelId || 'Modello';
  }, [modelOptions, currentModelId]);

  const toggleSection = useCallback((s: SideSection) => {
    setActiveSection(prev => (prev === s ? null : s));
  }, []);

  const closeSection = useCallback(() => {
    setActiveSection(current => {
      if (current) {
        window.setTimeout(() => tabRefs.current[current]?.focus(), 0);
      }
      return null;
    });
  }, []);

  const openArtifact = useCallback((id: string) => setSelectedArtifactId(id), []);
  const closeArtifact = useCallback(() => setSelectedArtifactId(null), []);

  // Selezionare una conversazione dall'overlay torna automaticamente alla chat.
  const handleSelectConversation = useCallback((id: string) => {
    onSelectConversation(id);
    closeSection();
  }, [onSelectConversation, closeSection]);

  const handleNewConversation = useCallback(() => {
    onNewConversation();
    closeSection();
  }, [onNewConversation, closeSection]);

  // Task #3822 — nuova chat dentro un progetto: crea, apre in chat e chiude l'overlay.
  const handleNewProjectConversation = useCallback(async (projectId: string) => {
    if (!onNewProjectConversation) return;
    await onNewProjectConversation(projectId);
    closeSection();
  }, [onNewProjectConversation, closeSection]);

  const handleOpenRoutineInChat = useCallback((routine?: PaRoutine) => {
    setActiveSection(null);
    onOpenRoutineInChat(routine);
    window.setTimeout(() => {
      shellRef.current
        ?.querySelector<HTMLTextAreaElement>('textarea[placeholder="Scrivi un messaggio…"]')
        ?.focus();
    }, 0);
  }, [onOpenRoutineInChat]);

  // Eliminazione artefatto: se è quello aperto nel viewer, chiude il viewer.
  const handleDeleteArtifact = useCallback((id: string) => {
    if (selectedArtifactId === id) setSelectedArtifactId(null);
    return onDeleteArtifact?.(id);
  }, [selectedArtifactId, onDeleteArtifact]);

  const activeArtifact = useMemo(() => {
    if (!selectedArtifactId) return null;
    return artifacts.find(a => a.id === selectedArtifactId) ?? null;
  }, [artifacts, selectedArtifactId]);

  useEffect(() => {
    if (!sectionRequest) return;
    setSelectedArtifactId(null);
    setActiveSection(sectionRequest.section);
  }, [sectionRequest]);

  useEffect(() => {
    if (!activeSection) return;
    const overlay = sectionOverlayRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlay?.querySelector<HTMLButtonElement>('[data-section-close]')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (document.querySelector('[role="alertdialog"], [role="listbox"]')) return;
        if (document.querySelector('[role="dialog"]:not([data-pa-section-overlay]):not([data-pa-artifact-overlay])')) return;
        if (selectedArtifactIdRef.current) return;
        event.preventDefault();
        closeSection();
        return;
      }
      if (event.key !== 'Tab' || selectedArtifactIdRef.current) return;
      const focusable = Array.from(
        overlay?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter(element => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (document.contains(previouslyFocused)) previouslyFocused?.focus();
    };
  }, [activeSection, closeSection]);

  useEffect(() => {
    if (!selectedArtifactId) return;
    const overlay = artifactOverlayRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlay?.querySelector<HTMLButtonElement>('[data-artifact-close]')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (document.querySelector('[role="dialog"]:not([data-pa-section-overlay]):not([data-pa-artifact-overlay])')) return;
        event.preventDefault();
        closeArtifact();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        overlay?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter(element => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (document.contains(previouslyFocused)) previouslyFocused?.focus();
    };
  }, [selectedArtifactId, closeArtifact]);

  return (
    <div ref={shellRef} className="fixed inset-0 flex flex-col bg-card" style={{ zIndex: Z_PA_SHELL }}>
      {/* Header compatto */}
      <div className="flex items-center justify-between px-2.5 py-2 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {/* Task #3041 — faccia del PA (icona ↔ avatar + stato); click = personalizza. */}
          <button
            type="button"
            onClick={onOpenAvatarSettings}
            title="Avatar assistente — personalizza"
            className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <PaFace
              avatarEnabled={avatarEnabled}
              avatarConfig={avatarConfig}
              defaultSeed={avatarDefaultSeed}
              color={assistantColor}
              FallbackIcon={AssistantIcon}
              state={faceState}
              size={32}
            />
          </button>
          <div className="leading-tight min-w-0">
            <div className="font-semibold text-sm text-foreground truncate">{assistantName}</div>
            <div className="text-[10px] text-muted-foreground truncate" title={currentConversationTitle ?? undefined}>
              {currentConversationTitle ?? 'Workspace AI'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {modelSwitchEnabled && modelOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 max-w-[120px] gap-1 px-1.5 text-[11px] font-medium text-foreground"
                  title="Modello per questa conversazione"
                >
                  <Brain className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{currentModelLabel}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: Z_PA_SHELL_DROPDOWN }} className="w-72 max-h-[60vh] overflow-y-auto">
                <DropdownMenuLabel className="text-[11px] text-muted-foreground">
                  Modello · questa conversazione
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {modelOptions.map((m) => {
                  const active = m.id === currentModelId;
                  const isDefault = m.id === defaultModelId;
                  const hint = m.description?.trim()
                    ? m.description
                    : (m.contextWindow ? `Contesto ~${Math.round(m.contextWindow / 1000)}k token` : '');
                  return (
                    <DropdownMenuItem
                      key={m.id}
                      onSelect={() => { if (!active) onModelChange?.(m.id); }}
                      title={m.tooltip || m.description || undefined}
                      className="flex flex-col items-start gap-0.5 py-1.5"
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="flex-1 truncate text-[12px] font-medium text-foreground">{m.displayName}</span>
                        {isDefault && (
                          <span className="rounded bg-muted px-1 text-[9px] uppercase tracking-wide text-muted-foreground">default</span>
                        )}
                        {active && <Check className="h-3.5 w-3.5 shrink-0 text-violet-600" />}
                      </div>
                      {hint && <span className="line-clamp-2 text-[10px] text-muted-foreground">{hint}</span>}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onCollapse} title="Minimizza">
            <Minus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} title="Chiudi">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Corpo: chat live (stream condiviso + composer condiviso) */}
      <div className="flex-1 flex flex-col min-h-0 bg-card">
        <ScrollArea className="flex-1">
          <ChatMessageStream
            assistantName={assistantName}
            assistantColor={assistantColor}
            assistantDescription={assistantDescription}
            userFirstName={userFirstName}
            AssistantIcon={AssistantIcon}
            messages={messages}
            streamingContent={streamingContent}
            streamingThinking={streamingThinking}
            activeTools={activeTools}
            activeRag={activeRag}
            isStreaming={isStreaming}
            streamingStatus={streamingStatus}
            conversationArtifacts={conversationArtifacts}
            approvals={approvals}
            onDecideApproval={onDecideApproval}
            conversationJobs={conversationJobs}
            jobsLoading={jobsLoading}
            jobsError={jobsError}
            onRetryJobs={onRetryJobs}
            onDecideJob={onDecideJob}
            onCancelJob={onCancelJob}
            onOpenArtifact={openArtifact}
            onRegenerate={onRegenerate}
            containerClassName="px-3 py-3 space-y-3 w-full"
            avatarEnabled={avatarEnabled}
            avatarConfig={avatarConfig}
            avatarDefaultSeed={avatarDefaultSeed}
            faceState={faceState}
          />
        </ScrollArea>

        <ResumeBar state={workspaceState} onResume={onResume} />

        <ChatComposer
          inputValue={inputValue}
          onInputChange={onInputChange}
          onSendMessage={onSendMessage}
          onInterrupt={onInterrupt}
          isStreaming={isStreaming}
          isRecording={isRecording}
          onToggleRecording={onToggleRecording}
          attachedFiles={attachedFiles}
          onUploadFiles={onUploadFiles}
          onRemoveFile={onRemoveFile}
          currentConversationId={currentConversationId}
          assistantId={assistantId}
          slashSkills={slashSkills}
          invokedSkills={invokedSkills}
          onInvokeSkill={onInvokeSkill}
          onRemoveInvokedSkill={onRemoveInvokedSkill}
          gutterClass="px-3"
        />
      </div>

      {/* Tab bar in basso */}
      <nav className="shrink-0 border-t border-border bg-card flex overflow-x-auto">
        {MOBILE_TABS.map(({ id, label, Icon }) => {
          const isActive = activeSection === id;
          const badge = id === 'approvals' ? approvals.length : id === 'artifacts' ? artifacts.length : 0;
          return (
            <button
              key={id}
              ref={element => {
                if (element) tabRefs.current[id] = element;
                else delete tabRefs.current[id];
              }}
              type="button"
              onClick={() => toggleSection(id)}
              className={`relative min-w-16 flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-medium transition ${
                isActive ? 'text-violet-700' : 'text-muted-foreground'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
              {badge > 0 && (
                <span className="absolute top-1 right-[18%] min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Overlay sezione full-screen (WorkspaceSidePanel condiviso) */}
      {activeSection && (
        <div
          ref={sectionOverlayRef}
          role="dialog"
          aria-modal="true"
          aria-label={SECTION_TITLE[activeSection]}
          data-pa-section-overlay
          className="fixed inset-0 flex flex-col bg-card"
          style={{ zIndex: Z_PA_SHELL_OVERLAY }}
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border shrink-0">
            <span className="text-sm font-semibold text-foreground">{SECTION_TITLE[activeSection]}</span>
            <Button data-section-close variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={closeSection} title="Chiudi">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 min-h-0">
            <WorkspaceSidePanel
              section={activeSection}
              conversations={conversations}
              currentConversationId={currentConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleNewConversation}
              onDeleteConversation={onDeleteConversation}
              onBulkDeleteConversations={onBulkDeleteConversations}
              rounds={rounds}
              artifacts={artifacts}
              selectedArtifactId={selectedArtifactId}
              onSelectArtifact={openArtifact}
              onDeleteArtifact={onDeleteArtifact ? handleDeleteArtifact : undefined}
              approvals={approvals}
              onDecideApproval={onDecideApproval}
              onOpenRoutineInChat={handleOpenRoutineInChat}
              assistantId={assistantId}
              currentUserId={currentUserId}
              onNewProjectConversation={onNewProjectConversation ? handleNewProjectConversation : undefined}
            />
          </div>
        </div>
      )}

      {/* Overlay artefatto full-screen (ArtifactPanel condiviso) */}
      {activeArtifact && (
        <div
          ref={artifactOverlayRef}
          role="dialog"
          aria-modal="true"
          aria-label={activeArtifact.title}
          data-pa-artifact-overlay
          className="fixed inset-0 flex flex-col bg-card"
          style={{ zIndex: Z_PA_SHELL_OVERLAY_TOP }}
        >
          <div className="flex items-center gap-2 px-2 py-2.5 border-b border-border shrink-0">
            <Button data-artifact-close variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={closeArtifact} title="Indietro">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground" title={activeArtifact.title}>
              {activeArtifact.title}
            </span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={closeArtifact} title="Chiudi">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 min-h-0">
            <ArtifactPanel
              artifact={activeArtifact}
              onAction={onArtifactAction}
              onFormSubmit={onArtifactFormSubmit}
              versions={artifacts.filter(a => a.slug === activeArtifact.slug && a.conversationId === activeArtifact.conversationId)}
              onSelectVersion={setSelectedArtifactId}
            />
          </div>
        </div>
      )}
    </div>
  );
};
